import type { FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';

import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag.hook';
import { FeatureFlagName } from '../../../feature-flags/models/feature-flag-names.enum';
import {
  selectMemberships,
  selectMembershipsLoaded,
} from '../../../membership/store/membership.selectors';
import { useAppSelector } from '../../../store/hooks';
import { ContentType } from '../../model/tab.interface';
import type { ContentTab } from '../../model/tab.interface';
import { selectContentTabs, selectContentTabsLoaded } from '../../store/perks.selectors';

export interface Props {}

export const PerksHomeComponent: FunctionComponent<Props> = ({}) => {
  const [, setLocation] = useLocation();
  const [hasNavigated, setHasNavigated] = useState(false);
  const contentTabsLoaded = useAppSelector(selectContentTabsLoaded);
  const membershipsLoaded = useAppSelector(selectMembershipsLoaded);
  const contentTabs = useAppSelector(selectContentTabs);
  const memberships = useAppSelector(selectMemberships);
  const isAudioStreamingEnabled = useFeatureFlag(FeatureFlagName.AUDIO_STREAMING);

  // Check if we're in the Shopify theme editor
  const isInThemeEditor = () => {
    try {
      return window.Shopify?.designMode || window.self !== window.top;
    } catch {
      return false;
    }
  };

  const navigateTo = (path: string) => {
    setLocation(path);
  };

  const tabToRoute = (tab: ContentTab): string | null => {
    switch (tab.contentType) {
      case ContentType.POST:
        return '/posts';
      case ContentType.VIDEO:
        return '/videos';
      case ContentType.ALBUM:
        return isAudioStreamingEnabled ? '/music' : null;
      case ContentType.PRODUCT:
        return '/merch';
      case ContentType.PROMOTION:
        return '/codes';
      case ContentType.CUSTOM:
        return tab.id ? `/pages/${tab.id}` : null;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (hasNavigated) return;
    if (isInThemeEditor()) {
      navigateTo('/join');
      setHasNavigated(true);
      return;
    }
    if (!contentTabsLoaded || !membershipsLoaded) return;
    if (!Array.isArray(memberships)) return;

    const anyActiveMemberships =
      memberships.length > 0 && memberships.some((m) => m.state === 'PUBLISHED');
    const noMembershipsWithContract = memberships.every((m) => !m.contractId);

    if (anyActiveMemberships && noMembershipsWithContract) {
      navigateTo('/join');
    } else {
      const sortedTabs = [...contentTabs].sort((a, b) => a.order - b.order);
      const firstRoute = sortedTabs.reduce<string | null>((acc, tab) => acc ?? tabToRoute(tab), null);
      navigateTo(firstRoute ?? '/my-account');
    }

    setHasNavigated(true);
  }, [contentTabsLoaded, membershipsLoaded, memberships, hasNavigated, setLocation, isAudioStreamingEnabled]);

  return <></>;
};